// src/components/forms/BuildingForm.tsx — a physical block within a scheme.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { api } from '../../lib/api';
import { useUserOptions } from '../../lib/options';
import { Button, Field, Input, Modal, Select } from '../ui';
import { FormError, FormGrid, Wide, nulled, numberOr } from './common';

type B = Record<string, any>;

const blank = { name: '', floors: '', heightMetres: '', isHigherRisk: false, bsrRegistrationNumber: '', accountablePersonId: '' };

/**
 * Buildings exist for one reason: the Building Safety Act 2022. A block at or above
 * 18 metres or seven storeys is higher-risk and carries registration and accountable
 * person duties — so the form works those out as you type rather than trusting a tick.
 */
export function BuildingForm({ open, onClose, propertyId, building }: { open: boolean; onClose: () => void; propertyId: string; building?: B }) {
  const isNew = !building;
  const qc = useQueryClient();
  const users = useUserOptions();
  const [form, setForm] = useState(() => (building
    ? {
        name: building.name ?? '', floors: building.floors == null ? '' : String(building.floors),
        heightMetres: building.heightMetres == null ? '' : String(building.heightMetres),
        isHigherRisk: !!building.isHigherRisk, bsrRegistrationNumber: building.bsrRegistrationNumber ?? '',
        accountablePersonId: building.accountablePersonId ?? '',
      }
    : blank));
  const set = (patch: Partial<typeof blank>) => setForm((f) => ({ ...f, ...patch }));

  // The statutory test, applied live. The user can still override it either way.
  const floors = numberOr(form.floors) ?? 0;
  const height = numberOr(form.heightMetres) ?? 0;
  const meetsTest = floors >= 7 || height >= 18;

  const payload = () => ({
    ...(isNew ? { propertyId } : {}),
    name: form.name.trim(),
    floors: numberOr(form.floors) ?? null,
    heightMetres: numberOr(form.heightMetres) ?? null,
    isHigherRisk: form.isHigherRisk,
    bsrRegistrationNumber: nulled(form.bsrRegistrationNumber),
    accountablePersonId: nulled(form.accountablePersonId),
  });

  const save = useMutation({
    mutationFn: async () => (isNew
      ? (await api.post<B>('/buildings', payload())).data
      : (await api.patch<B>(`/buildings/${building!.id}`, payload())).data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['/buildings'] });
      void qc.invalidateQueries({ queryKey: ['options', '/buildings'] });
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isNew ? 'Add a building' : `Edit ${building!.name}`}
      footer={<>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={!form.name.trim() || save.isPending}>{save.isPending ? 'Saving…' : isNew ? 'Add building' : 'Save changes'}</Button>
      </>}
    >
      <div className="space-y-5">
        <FormGrid>
          <Wide><Field label="Building name"><Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Block A" className="w-full" /></Field></Wide>
          <Field label="Storeys"><Input value={form.floors} onChange={(e) => set({ floors: e.target.value })} inputMode="numeric" placeholder="9" className="w-full" /></Field>
          <Field label="Height (metres)"><Input value={form.heightMetres} onChange={(e) => set({ heightMetres: e.target.value })} inputMode="decimal" placeholder="27.5" className="w-full" /></Field>

          <Wide>
            <label className="flex items-start gap-2.5 rounded-xl border border-ink-200 bg-ink-50/60 px-3.5 py-3 text-[13px] text-ink-700">
              <input type="checkbox" checked={form.isHigherRisk} onChange={(e) => set({ isHigherRisk: e.target.checked })} className="mt-0.5 h-4 w-4 accent-ink-900" />
              <span>
                Higher-risk building
                <span className="mt-0.5 block text-[11.5px] leading-relaxed text-ink-500">18 metres or more, or seven storeys or more. Registration with the Building Safety Regulator and a named accountable person are then required.</span>
              </span>
            </label>
            {meetsTest && !form.isHigherRisk && (
              <p className="mt-2 flex items-start gap-1.5 text-[12px] text-amber-700">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {floors >= 7 ? `${floors} storeys` : `${height}m`} meets the higher-risk test. Tick the box unless you know otherwise.
              </p>
            )}
          </Wide>

          {form.isHigherRisk && <>
            <Field label="BSR registration number"><Input value={form.bsrRegistrationNumber} onChange={(e) => set({ bsrRegistrationNumber: e.target.value })} placeholder="HRB123456789" className="w-full" /></Field>
            <Field label="Accountable person"><Select value={form.accountablePersonId} onChange={(e) => set({ accountablePersonId: e.target.value })} className="w-full"><option value="">Not yet named</option>{(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}</Select></Field>
          </>}
        </FormGrid>

        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
